import { useState } from 'react';
import { Eye } from 'lucide-react';
import type { Transaction } from '../types';
import { Button, Money, Tag } from './ui';
import SourcePageViewer from './SourcePageViewer';
import { formatTxnDate } from '../utils/dates';
import { formatAccountName } from '../utils/accounts';
import { useTransactionRawText } from '../hooks/useTransactions';
import { propertyStatusTone, propertyStatusTooltip } from '../lib/enums';

interface TransactionDetailProps {
  transaction: Transaction;
  onOverride: (transaction: Transaction) => void;
}


function getConfidenceColor(score: number | null) {
  if (score === null) return 'text-ink-faint';
  if (score >= 80) return 'text-green-700';
  if (score >= 50) return 'text-clay-ink';
  return 'text-danger';
}

export default function TransactionDetail({ transaction, onOverride }: TransactionDetailProps) {
  const [viewerOpen, setViewerOpen] = useState(false);

  const needsRawText = transaction.raw_text == null;
  const { data: rawTextData } = useTransactionRawText(transaction.id, needsRawText);
  const rawText = transaction.raw_text ?? rawTextData?.raw_text ?? null;

  const canViewSource = transaction.statement_id && transaction.source_page !== null;

  return (
    <div className="space-y-4 rounded-lg border border-border bg-surface-page p-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-3">
          <div>
            <p className="t-eyebrow">Description</p>
            <p className="text-sm text-ink">{transaction.description}</p>
          </div>
          <div>
            <p className="t-eyebrow">Amount</p>
            <p className="text-sm font-semibold">
              <Money value={transaction.amount} tone="auto" />
              {transaction.amount >= 0 ? ' (Credit)' : ' (Debit)'}
            </p>
          </div>
          <div>
            <p className="t-eyebrow">Date</p>
            <p className="text-sm text-ink">{formatTxnDate(transaction.date)}</p>
          </div>
          <div>
            <p className="t-eyebrow">Account</p>
            <p className="text-sm text-ink">{formatAccountName(transaction.bank_label)}</p>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <p className="t-eyebrow">Property Status</p>
            <Tag
              size="sm"
              tone={propertyStatusTone(transaction.property_status)}
              title={propertyStatusTooltip(transaction.property_status)}
            >
              {transaction.property_status_display}
            </Tag>
            {transaction.is_override && (
              <span className="ml-2 text-xs text-clay-ink">(Manual Override)</span>
            )}
          </div>
          <div>
            <p className="t-eyebrow">Confidence Score</p>
            <p className={`text-sm font-semibold ${getConfidenceColor(transaction.confidence_score)}`}>
              {transaction.confidence_score !== null ? `${transaction.confidence_score}%` : 'N/A'}
            </p>
          </div>
          {transaction.merchant_name && (
            <div>
              <p className="t-eyebrow">Merchant</p>
              <p className="text-sm text-ink">
                {transaction.merchant_name}
                {transaction.merchant_category && (
                  <span className="text-ink-faint"> ({transaction.merchant_category})</span>
                )}
              </p>
            </div>
          )}
          {transaction.running_balance !== null && (
            <div>
              <p className="t-eyebrow">Running Balance</p>
              <p className="text-sm text-ink">
                <Money value={transaction.running_balance} />
              </p>
            </div>
          )}
        </div>
      </div>

      {rawText && (
        <div className="border-t border-border-subtle pt-3">
          <p className="t-eyebrow mb-1">Original Statement Text</p>
          <p className="rounded-control border border-border-subtle bg-surface-card p-2 font-mono text-xs text-ink-soft">
            {rawText}
          </p>
        </div>
      )}

      {transaction.source_file && (
        <div className="border-t border-border-subtle pt-3">
          <p className="t-eyebrow mb-1">Source</p>
          {canViewSource ? (
            /* eslint-disable-next-line no-restricted-syntax -- inline citation link: no ui/LinkButton primitive exists yet */
            <button
              onClick={() => setViewerOpen(true)}
              className="flex items-center gap-1 text-sm text-green-700 transition-colors hover:text-green-900 hover:underline"
            >
              <Eye size={16} />
              {transaction.source_file}
              {transaction.source_page !== null && ` (Page ${transaction.source_page})`}
            </button>
          ) : (
            <p className="text-sm text-ink-soft">
              {transaction.source_file}
              {transaction.source_page !== null && ` (Page ${transaction.source_page})`}
            </p>
          )}
        </div>
      )}

      {transaction.override_reason && (
        <div className="border-t border-border-subtle pt-3">
          <p className="t-eyebrow mb-1">Override Reason</p>
          <p className="rounded-control bg-clay-50 p-2 text-sm text-clay-ink">{transaction.override_reason}</p>
          {transaction.property_reviewed_at && (
            <p className="mt-1 text-2xs text-ink-faint">
              Overridden{transaction.property_reviewed_by && transaction.property_reviewed_by.includes('@')
                ? ` by ${transaction.property_reviewed_by}`
                : ''}{' '}
              on {new Date(transaction.property_reviewed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </p>
          )}
        </div>
      )}

      {transaction.waste_flag && (
        <div className="border-t border-border-subtle pt-3">
          <p className="t-eyebrow mb-1">Flagged as Potential Waste</p>
          <div className="flex items-center gap-2">
            <Tag
              size="sm"
              tone={transaction.waste_severity === 'high' ? 'danger' : transaction.waste_severity === 'medium' ? 'warn' : 'info'}
            >
              {transaction.waste_severity || 'flagged'}
            </Tag>
            {transaction.waste_category && (
              <span className="text-sm text-ink-soft">{transaction.waste_category}</span>
            )}
          </div>
        </div>
      )}

      {transaction.transfer_match_id && (
        <div className="border-t border-border-subtle pt-3">
          <p className="t-eyebrow mb-1">Transfer Match</p>
          <p className="text-sm text-ink-soft">
            Linked to transaction #{transaction.transfer_match_id}
            {transaction.transfer_match_confidence && ` (${transaction.transfer_match_confidence} confidence)`}
          </p>
        </div>
      )}

      <div className="flex justify-end border-t border-border-subtle pt-3">
        <Button variant="secondary" onClick={() => onOverride(transaction)}>
          Override Classification
        </Button>
      </div>

      {canViewSource && (
        <SourcePageViewer
          statementId={transaction.statement_id}
          pageNumber={transaction.source_page!}
          sourceFile={transaction.source_file}
          isOpen={viewerOpen}
          onClose={() => setViewerOpen(false)}
        />
      )}
    </div>
  );
}
